/**
 * Havjoy Personalize - Shared core utilities
 * Used by both collection and product personalize scripts
 */
window.HavjoyCore = (function() {
'use strict';

const API_BASE_URL = 'https://api.havjoy.com';
const VARIANT_DIGITAL = 'High Quality Digital File';
const SIZE_MAP = { '9x12': '9in x 12in', '12x16': '12in x 16in', '16x20': '16in x 20in' };
const MAX_GENERATED = 20;
const DOWNSCALE_THRESHOLD = 1536;
const DOWNSCALE_TARGET = 768;
const RECENT_UPLOADS_KEY = 'havjoy_recent_uploads';
var _lockedScrollY = 0;
var _scrollUnlockBound = false;

// ===== UPLOADER =====
class Uploader {
  constructor() {
    this.uploadApi = `${API_BASE_URL}/api/upload-supabase`;
    this.uploadCache = new Map();
  }
  isHeic(f) {
    return f.type.includes('heic') || f.type.includes('heif') || /\.heic$/i.test(f.name) || /\.heif$/i.test(f.name);
  }
  async getFileHash(blob) {
    const size = blob.size;
    const [first, last] = await Promise.all([
      blob.slice(0, 1024).arrayBuffer(),
      blob.slice(Math.max(0, size - 1024), size).arrayBuffer()
    ]);
    return size + '_' + Array.from(new Uint8Array(first)).slice(0, 10).join('') + '_' + Array.from(new Uint8Array(last)).slice(0, 10).join('');
  }
  async upload(blob) {
    const hash = await this.getFileHash(blob);
    if (this.uploadCache.has(hash)) {
      const cached = this.uploadCache.get(hash);
      if (Date.now() - cached.timestamp < 10 * 60 * 1000) return { url: cached.url };
      this.uploadCache.delete(hash);
    }
    const maxRetries = 2;
    let lastError;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const fd = new FormData();
        fd.append('file', blob, 'image.jpg');
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 60000);
        const r = await fetch(this.uploadApi, { method: 'POST', body: fd, signal: controller.signal });
        clearTimeout(timeout);
        if (!r.ok) throw new Error(`Upload failed: ${r.status}`);
        const result = await r.json();
        this.uploadCache.set(hash, { url: result.url, timestamp: Date.now() });
        if (this.uploadCache.size > 20) this.uploadCache.delete(this.uploadCache.keys().next().value);
        return result;
      } catch (error) {
        lastError = error;
        if (attempt < maxRetries && !(error instanceof DOMException && error.name === 'AbortError')) {
          await new Promise(r => setTimeout(r, 1000 * attempt));
        }
      }
    }
    throw lastError;
  }
}

// ===== HELPERS =====
function showToast(message, type, duration) {
  type = type || 'error';
  duration = duration || 4000;
  var openModal = document.querySelector('dialog#personalize-modal[open]');
  var el = openModal ? openModal.querySelector('.toast-container') : document.getElementById('toast-container');
  if (!el) {
    el = document.createElement('div');
    if (!openModal) el.id = 'toast-container';
    el.className = 'toast-container';
    (openModal ? openModal.querySelector('.modal-content') || openModal : document.body).appendChild(el);
  }
  const toast = document.createElement('div');
  toast.className = 'toast ' + type;
  toast.textContent = message;
  if (duration !== 4000) {
    var delay = (duration - 500) / 1000;
    toast.style.animation = 'toastIn .3s ease, toastOut .3s ease ' + delay + 's forwards';
  }
  el.appendChild(toast);
  setTimeout(function() { toast.remove(); }, duration);
}

function lockPageScroll() {
  var persModal = document.querySelector('dialog#personalize-modal');
  if (persModal && !_scrollUnlockBound) {
    _scrollUnlockBound = true;
    persModal.addEventListener('close', function() {
      window.setTimeout(function() {
        if (!document.querySelector('dialog#personalize-modal[open]')) unlockPageScroll();
      }, 0);
    });
  }

  if (document.body.classList.contains('hj-modal-open')) return;
  _lockedScrollY = window.scrollY || window.pageYOffset || 0;
  document.documentElement.classList.add('hj-modal-open');
  document.body.classList.add('hj-modal-open');
  document.body.style.position = 'fixed';
  document.body.style.top = '-' + _lockedScrollY + 'px';
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
}

function unlockPageScroll() {
  if (!document.body.classList.contains('hj-modal-open')) return;
  document.documentElement.classList.remove('hj-modal-open');
  document.body.classList.remove('hj-modal-open');
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  window.scrollTo(0, _lockedScrollY || 0);
  _lockedScrollY = 0;
}

function showErrorModal(errorType, persModal) {
  if (persModal && persModal.open) persModal.close();

  var existing = document.getElementById('havjoy-error-modal');
  if (existing) existing.remove();

  var el = document.createElement('div');
  el.id = 'havjoy-error-modal';
  el.className = 'havjoy-error-modal';
  var shouldRefreshOnClose = errorType === 'gpt_image_422';

  function closeErrorModal() {
    el.remove();
    if (shouldRefreshOnClose) window.location.reload();
  }

  function refreshThenOpenPicker() {
    try {
      window.sessionStorage.setItem('hjOpenPickerAfterReload', '1');
    } catch (error) {}
    closeErrorModal();
  }

  if (errorType === 'gpt_image_422') {
    el.innerHTML =
      '<div class="havjoy-error-modal__box">' +
        '<button type="button" class="havjoy-error-modal__close" aria-label="Close">\u00d7</button>' +
        '<p class="havjoy-error-modal__icon">\uD83D\uDDBC\uFE0F</p>' +
        '<h3 class="havjoy-error-modal__title">Image Not Accepted</h3>' +
        '<p class="havjoy-error-modal__body">Please upload a clear photo and make sure it does not contain sensitive content (nudity, blood, etc.).</p>' +
        '<button type="button" class="vopt-btn havjoy-error-action">\uD83D\uDCF7 Upload New Photo</button>' +
      '</div>';
  } else if (errorType === 'rate_limited') {
    el.innerHTML =
      '<div class="havjoy-error-modal__box havjoy-error-modal__box--packs">' +
        '<button type="button" class="havjoy-error-modal__close" aria-label="Close">\u00d7</button>' +
        '<h3 class="havjoy-error-modal__title">Usage Limit Reached</h3>' +
        '<a href="/pages/my-artwork" class="havjoy-error-modal__artwork-banner">' +
          '<span class="havjoy-error-modal__artwork-banner-icon"><svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></span>' +
          '<span class="havjoy-error-modal__artwork-banner-text">' +
            '<strong>Your artwork is saved!</strong>' +
            '<span>Tap to view &amp; purchase it anytime</span>' +
          '</span>' +
          '<span class="havjoy-error-modal__artwork-banner-arrow">\u203A</span>' +
        '</a>' +
        '<p class="havjoy-error-modal__body">Come back in <strong>2 hours</strong>, or upgrade your plan:</p>' +
        '<div class="vopt-pack-mini-card">' +
          '<div class="vopt-pack-mini-top">' +
            '<div><div class="vopt-pack-mini-title">Digital Pack</div><div class="vopt-pack-mini-sub">5 digital files \u00b7 $7.99/file</div></div>' +
            '<div class="vopt-pack-mini-price">$39.95</div>' +
          '</div>' +
          '<ul class="vopt-pack-mini-features"><li>\u2713 5 digital files included</li><li>\u2713 Instant high-res download</li><li>\u2713 Unlimited artwork generation</li></ul>' +
          '<button type="button" class="vopt-btn" data-variant-id="48213661581544" onclick="havjoyPackAtc(this)"><span>Get Digital Pack \u2014 $39.95</span></button>' +
        '</div>' +
        '<div class="vopt-pack-mini-card vopt-pack-mini-card--highlight">' +
          '<div class="vopt-pack-mini-badge">BEST VALUE</div>' +
          '<div class="vopt-pack-mini-top">' +
            '<div><div class="vopt-pack-mini-title">Studio Pack</div><div class="vopt-pack-mini-sub">20 digital files \u00b7 $4.99/file</div></div>' +
            '<div class="vopt-pack-mini-price">$99.95</div>' +
          '</div>' +
          '<ul class="vopt-pack-mini-features"><li>\u2713 20 digital files included</li><li>\u2713 Instant high-res download</li><li>\u2713 Unlimited artwork generation</li></ul>' +
          '<button type="button" class="vopt-btn" data-variant-id="48213661614312" onclick="havjoyPackAtc(this)"><span>Get Studio Pack \u2014 $99.95</span></button>' +
        '</div>' +
      '</div>';
  } else {
    var icon = errorType === 'timeout' ? '\u23F3' : '\uD83D\uDD27';
    var title = errorType === 'timeout' ? 'Server Overloaded' : 'Service Unavailable';
    var body = errorType === 'timeout'
      ? 'Our servers are currently busy. Please try again in a few minutes.'
      : 'We\u2019re performing maintenance. Please check back later.';
    el.innerHTML =
      '<div class="havjoy-error-modal__box">' +
        '<p class="havjoy-error-modal__icon">' + icon + '</p>' +
        '<h3 class="havjoy-error-modal__title">' + title + '</h3>' +
        '<p class="havjoy-error-modal__body">' + body + '</p>' +
        '<button type="button" class="btn btn-confirm havjoy-error-action">OK</button>' +
      '</div>';
  }

  document.body.appendChild(el);

  if (errorType === 'gpt_image_422') {
    el.querySelector('.havjoy-error-action').addEventListener('click', function() {
      refreshThenOpenPicker();
    });
    var imageErrorClose = el.querySelector('.havjoy-error-modal__close');
    if (imageErrorClose) imageErrorClose.addEventListener('click', closeErrorModal);
  } else if (errorType === 'rate_limited') {
    el.querySelector('.havjoy-error-modal__close').addEventListener('click', function() { el.remove(); });
  } else {
    el.querySelector('.havjoy-error-action').addEventListener('click', function() { el.remove(); });
  }

  document.addEventListener('keydown', function onEsc(e) {
    if (e.key === 'Escape') { closeErrorModal(); document.removeEventListener('keydown', onEsc); }
  });
}

function formatMoney(cents) { return '$' + (cents / 100).toFixed(2); }

function buildCartProperties(posData) {
  const props = { '_your_design': posData.personalizeImage };
  if (posData.options) {
    const labels = posData.optionLabels || {};
    for (const [key, value] of Object.entries(posData.options)) {
      props[labels[key] || key] = value;
    }
  }
  return props;
}

async function submitToCart(items) {
  const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items })
  });
  if (!response.ok) {
    const errorData = await response.json().catch(function() { return {}; });
    throw new Error(errorData.description || ('HTTP ' + response.status));
  }
  if (typeof CartDrawerController !== 'undefined') {
    CartDrawerController.open();
    CartDrawerController.update().catch(console.error);
  } else {
    window.location.href = '/cart';
  }
}

function variantTitle(type, sizeKey) {
  const size = SIZE_MAP[sizeKey] || SIZE_MAP['9x12'];
  const patterns = {
    digital: VARIANT_DIGITAL,
    framed: 'Poster + Frame (' + size + ') + Digital File',
    canvas: 'Wooden Canvas (' + size + ') + Digital File'
  };
  return patterns[type] || patterns.digital;
}

function sizeFromTitle(title) {
  if (title.includes('9in x 12in'))  return '9x12';
  if (title.includes('12in x 16in')) return '12x16';
  if (title.includes('16in x 20in')) return '16x20';
  return '9x12';
}

// ===== IMAGE HELPERS =====
function isOptimalJpeg(file) {
  return (file.type === 'image/jpeg' || file.type === 'image/jpg') && file.size < 3 * 1024 * 1024;
}

async function convertToJpeg(file, maxWidth) {
  maxWidth = maxWidth || 1200;
  return new Promise(function(resolve, reject) {
    var img = new Image();
    var objectUrl = URL.createObjectURL(file);
    img.onload = function() {
      URL.revokeObjectURL(objectUrl);
      var canvas = document.createElement('canvas');
      var ctx = canvas.getContext('2d');
      var w = img.width, h = img.height;
      var longest = Math.max(w, h);
      if (longest > DOWNSCALE_THRESHOLD) {
        if (w >= h) { h = Math.round(h * DOWNSCALE_TARGET / w); w = DOWNSCALE_TARGET; }
        else { w = Math.round(w * DOWNSCALE_TARGET / h); h = DOWNSCALE_TARGET; }
      } else if (w > maxWidth) {
        h = Math.round((h * maxWidth) / w); w = maxWidth;
      }
      canvas.width = w; canvas.height = h;
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(function(blob) { blob ? resolve(blob) : reject(new Error('Failed to convert')); }, 'image/jpeg', 0.80);
    };
    img.onerror = function() { URL.revokeObjectURL(objectUrl); reject(new Error('Failed to load image')); };
    img.src = objectUrl;
  });
}

function checkNeedsResize(blob) {
  return new Promise(function(resolve) {
    var img = new Image();
    var url = URL.createObjectURL(blob);
    img.onload = function() { URL.revokeObjectURL(url); resolve(Math.max(img.naturalWidth, img.naturalHeight) > DOWNSCALE_THRESHOLD); };
    img.onerror = function() { URL.revokeObjectURL(url); resolve(false); };
    img.src = url;
  });
}

// ===== UPLOAD STATUS (works with any container element) =====
function showUploadStatus(container, text) {
  var status = container.querySelector('.upload-status');
  status.querySelector('.status-text').textContent = text;
  status.querySelector('.status-fill').style.width = '0%';
  status.classList.add('active');
}
function updateUploadStatus(container, text, percent) {
  var status = container.querySelector('.upload-status');
  if (text) status.querySelector('.status-text').textContent = text;
  if (percent !== undefined) status.querySelector('.status-fill').style.width = percent + '%';
}
function hideUploadStatus(container) {
  container.querySelector('.upload-status').classList.remove('active');
}

// ===== FILE UPLOAD FLOW =====
async function handleFileUpload(file, uploader, container, callbacks) {
  if (!file.type.startsWith('image/') && !uploader.isHeic(file)) {
    showToast('Please select an image file (JPG, PNG, HEIC, etc.)', 'warning');
    return null;
  }
  if (file.size > 20 * 1024 * 1024) {
    showToast('Image too large. Please select an image under 20MB.', 'warning');
    return null;
  }

  var fileToUpload = file;

  if (!isOptimalJpeg(file)) {
    if (uploader.isHeic(file)) {
      showUploadStatus(container, 'Converting photo format...');
      updateUploadStatus(container, null, 20);
      if (!window.heic2any) {
        // Singleton promise prevents duplicate script loads on rapid concurrent uploads
        if (!window.__heic2any_loading__) {
          window.__heic2any_loading__ = new Promise(function(res, rej) {
            var s = document.createElement('script');
            s.src = window.HavjoyHeic2anyUrl || 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
            s.onload = res; s.onerror = rej;
            document.head.appendChild(s);
          });
        }
        try {
          await window.__heic2any_loading__;
        } catch(e) {
          console.warn('[Havjoy] heic2any failed to load, uploading original file', e);
          // fall through — upload raw file without HEIC conversion
        }
      }
      fileToUpload = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.80 }).then(function(b) { return Array.isArray(b) ? b[0] : b; });
      updateUploadStatus(container, 'Photo converted', 40);
      if (await checkNeedsResize(fileToUpload)) {
        updateUploadStatus(container, 'Resizing photo...', 50);
        fileToUpload = await convertToJpeg(fileToUpload);
        updateUploadStatus(container, 'Photo resized', 65);
      }
    } else {
      showUploadStatus(container, 'Optimizing image...');
      updateUploadStatus(container, null, 20);
      fileToUpload = await convertToJpeg(file);
      updateUploadStatus(container, 'Image optimized', 40);
    }
    if (!fileToUpload || fileToUpload.size === 0) throw new Error('Image conversion failed');
  } else {
    if (await checkNeedsResize(file)) {
      showUploadStatus(container, 'Resizing image...');
      updateUploadStatus(container, null, 20);
      fileToUpload = await convertToJpeg(file);
      updateUploadStatus(container, 'Image resized', 40);
    } else {
      showUploadStatus(container, 'Preparing upload...');
      updateUploadStatus(container, null, 30);
    }
  }

  updateUploadStatus(container, 'Uploading your photo...', 50);
  var res = await uploader.upload(fileToUpload);
  updateUploadStatus(container, 'Upload complete', 100);
  if (res && res.url) saveRecentUploadedUrls([res.url]);
  return res;
}

// ===== SESSION STORAGE =====
function saveSessionToStorage(sessionId, productHandle) {
  try {
    var sessions = JSON.parse(localStorage.getItem('havjoy_sessions') || '[]');
    if (sessions.some(function(s) { return s.sessionId === sessionId; })) return;
    sessions.unshift({ sessionId: sessionId, productHandle: productHandle, savedAt: new Date().toISOString() });
    if (sessions.length > 50) sessions.length = 50;
    localStorage.setItem('havjoy_sessions', JSON.stringify(sessions));
  } catch(e) { console.warn('saveSessionToStorage failed:', e); }
}

function normalizeUploadedUrls(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    try {
      var parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
      if (parsed && typeof parsed === 'object') return extractUploadedUrls(parsed);
    } catch(e) {}
    return [value];
  }
  if (typeof value === 'object') return extractUploadedUrls(value);
  return [];
}

function extractUploadedUrls(source) {
  if (!source || typeof source !== 'object') return [];
  var urls = [];
  [
    source.customer_urls,
    source.customerUrls,
    source.customer_url,
    source.customerUrl,
    source.uploaded_urls,
    source.uploadedUrls,
    source.uploaded_url,
    source.uploadedUrl,
    source.original_urls,
    source.originalUrls,
    source.original_url,
    source.originalUrl
  ].forEach(function(value) {
    urls = urls.concat(normalizeUploadedUrls(value));
  });

  ['input', 'inputs', 'request', 'payload', 'params', 'metadata', 'form_data', 'formData', 'data'].forEach(function(key) {
    if (source[key] && source[key] !== source) urls = urls.concat(extractUploadedUrls(source[key]));
  });

  var seen = {};
  return urls.filter(function(url) {
    if (!url || typeof url !== 'string' || seen[url]) return false;
    seen[url] = true;
    return true;
  });
}

function saveRecentUploadedUrls(urls) {
  urls = normalizeUploadedUrls(urls);
  if (!urls.length) return;
  try {
    var rows = JSON.parse(localStorage.getItem(RECENT_UPLOADS_KEY) || '[]');
    if (!Array.isArray(rows)) rows = [];
    rows.unshift({ urls: urls, savedAt: new Date().toISOString() });
    if (rows.length > 20) rows.length = 20;
    localStorage.setItem(RECENT_UPLOADS_KEY, JSON.stringify(rows));
  } catch(e) {
    console.warn('saveRecentUploadedUrls failed:', e);
  }
}

function readRecentUploadedUrls() {
  try {
    var rows = JSON.parse(localStorage.getItem(RECENT_UPLOADS_KEY) || '[]');
    if (!Array.isArray(rows) || !rows.length) return [];
    return normalizeUploadedUrls(rows[0].urls || rows[0].url);
  } catch(e) {
    return [];
  }
}

function readLatestSessionId() {
  try {
    var sessions = JSON.parse(localStorage.getItem('havjoy_sessions') || '[]');
    if (!Array.isArray(sessions) || !sessions.length) return null;
    return sessions[0] && sessions[0].sessionId;
  } catch(e) {
    return null;
  }
}

async function getLatestUploadedUrls() {
  var sessionId = readLatestSessionId();
  if (!sessionId) return readRecentUploadedUrls();

  try {
    var r = await fetch(API_BASE_URL + '/session_info?sessionId=' + encodeURIComponent(sessionId));
    if (!r.ok) throw new Error('HTTP ' + r.status);
    var data = await r.json();
    var session = Array.isArray(data.sessions) ? data.sessions[0] : data;
    if (!session) return readRecentUploadedUrls();

    var urls = extractUploadedUrls(session);
    return urls.length ? urls : readRecentUploadedUrls();
  } catch(e) {
    console.warn('getLatestUploadedUrls failed:', e);
    return readRecentUploadedUrls();
  }
}

// ===== CLIENT IP (fetched lazily on first processImage call) =====
var _clientIp = null;
var _ipFetchStarted = false;
function _fetchClientIpLazy() {
  if (_ipFetchStarted) return;
  _ipFetchStarted = true;
  fetch('https://api.ipify.org?format=json')
    .then(function(r) { return r.json(); })
    .then(function(d) { _clientIp = d.ip; })
    .catch(function() {});
}

// ===== STEP INDICATOR =====
function setModalStep(modal, n) {
  modal.querySelectorAll('.hj-step').forEach(function(step) {
    var s = parseInt(step.dataset.step);
    step.classList.toggle('done', s < n);
    step.classList.toggle('active', s === n);
    var dot = step.querySelector('.hj-step-dot');
    if (dot) dot.innerHTML = s < n ? '&#10003;' : s;
  });
}

function syncButtonUI(scope, selector, dataAttr, value) {
  scope.querySelectorAll(selector).forEach(function(btn) {
    btn.classList.toggle('active', btn.dataset[dataAttr] === value);
  });
}

// ===== PROCESS IMAGE / POLLING =====
function processImage(opts) {
  // opts: { posData, modal, intervals, key, onComplete, onFail }
  _fetchClientIpLazy();
  if (opts.intervals[opts.key] && (opts.intervals[opts.key].progress || opts.intervals[opts.key].poll)) return;

  var modal = opts.modal;
  var posData = opts.posData;
  modal.querySelector('.generate-btn').disabled = true;
  modal.querySelector('.main-image').style.display = 'none';
  modal.querySelector('.gallery-image').style.display = 'none';

  var sessionId = 'sess_' + Math.random().toString(36).substr(2, 9);
  var uploadedPreview = modal.querySelector('.uploaded-image-preview');
  if (uploadedPreview) uploadedPreview.style.display = 'none';

  var hacard = modal.querySelector('.hacard');
  var loadingArea = modal.querySelector('.loading-area');
  var progressBar = modal.querySelector('.progress');
  hacard.style.display = 'block';
  loadingArea.style.display = 'flex';
  progressBar.style.width = '0%';

  if (posData.uploadedUrl) {
    loadingArea.style.backgroundImage = 'url(' + posData.uploadedUrl + ')';
    loadingArea.classList.add('with-photo');
  }

  var width = 0;
  var progressInterval = setInterval(function() {
    if (width < 90) { width += 1; progressBar.style.width = width + '%'; }
  }, 500);

  var loadingMessages = [
    'Analyzing your photo...',
    'Sketching the outline...',
    'Adding colors and details...',
    'Our artist is adding the final touches...',
    'Almost done — your portrait is coming to life!'
  ];
  var loadingTextEl = loadingArea.querySelector('.loading-text');
  var msgIndex = 0;
  var msgInterval = null;
  if (loadingTextEl) {
    loadingTextEl.textContent = loadingMessages[0];
    msgInterval = setInterval(function() {
      msgIndex = Math.min(msgIndex + 1, loadingMessages.length - 1);
      loadingTextEl.textContent = loadingMessages[msgIndex];
    }, 6000);
  }

  // Set synchronously so guard blocks re-entry and closeModal can always clear
  opts.intervals[opts.key] = { progress: progressInterval, poll: null, msg: msgInterval };

  var formData = new FormData();
  if (Object.keys(posData.options).length > 0) formData.append('options', JSON.stringify(posData.options));
  formData.append('sessionId', sessionId);
  formData.append('product_handle', posData.productHandle);
  if (Array.isArray(posData.uploadedUrls) && posData.uploadedUrls.length) {
    formData.append('customer_urls', JSON.stringify(posData.uploadedUrls));
    posData.uploadedUrls.forEach(function(url) {
      formData.append('customer_urls[]', url);
    });
    formData.append('customer_urls_count', String(posData.uploadedUrls.length));
  } else {
    formData.append('customer_url', posData.uploadedUrl);
  }
  formData.append('test', 'true');
  formData.append('source', 'shopify');
  if (_clientIp) formData.append('ip', _clientIp);

  fetch(API_BASE_URL + '/process_with_detect', { method: 'POST', body: formData })
    .then(function(response) {
      if (response.status === 429) { response._is429 = true; }
      if (!response.ok) throw response;
      return response.json();
    })
    .then(function(result) {
      if (result.error) {
        clearIntervals(opts.intervals, opts.key);
        loadingArea.style.display = 'none';
        modal.querySelector('.generate-btn').disabled = false;
      } else {
        startPolling(opts, sessionId);
      }
    })
    .catch(function(e) {
      clearIntervals(opts.intervals, opts.key);
      loadingArea.style.display = 'none';
      if (e && (e.status === 429 || e._is429)) {
        showErrorModal('rate_limited', modal);
      } else {
        modal.querySelector('.generate-btn').disabled = false;
      }
    });
}

function startPolling(opts, sessionId) {
  var checking = false;
  var pollInterval = setInterval(function() { poll(); }, 2500);
  if (opts.intervals[opts.key]) opts.intervals[opts.key].poll = pollInterval;
  poll();

  function poll() {
    if (checking) return;
    checking = true;
    var modal = opts.modal;
    var posData = opts.posData;

    fetch(API_BASE_URL + '/check_job_status?sessionId=' + sessionId)
      .then(function(r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function(result) {
        if (result.status === 'completed') {
          clearIntervals(opts.intervals, opts.key);
          var imageUrl = result.result_url;
          var mainImage = modal.querySelector('.main-image');

          // Preload image before showing — eliminates blank flash
          var preloader = new Image();
          preloader.onload = function() {
            var la = modal.querySelector('.loading-area');
            la.style.display = 'none';
            la.style.backgroundImage = '';
            la.classList.remove('with-photo');
            mainImage.src = imageUrl;
            mainImage.style.display = 'block';
            modal.querySelector('.gallery-image').style.display = '';

            posData.listGenerated.push(imageUrl);
            if (posData.listGenerated.length > MAX_GENERATED) posData.listGenerated.shift();
            posData.galleryIndex = posData.listGenerated.length - 1;
            posData.personalizeImage = imageUrl;

            if (posData.listGenerated.length > 1) {
              modal.querySelector('.prev-btn').style.visibility = 'visible';
              modal.querySelector('.next-btn').style.visibility = 'visible';
            }
            modal.querySelector('.generate-btn').disabled = false;
            saveSessionToStorage(sessionId, posData.productHandle);
            if (opts.onComplete) opts.onComplete(imageUrl, sessionId);
          };
          preloader.onerror = preloader.onload; // show even if load event misfires
          preloader.src = imageUrl;
        } else if (result.status === 'failed') {
          clearIntervals(opts.intervals, opts.key);
          modal.querySelector('.loading-area').style.display = 'none';
          var msg = result.error || '';
          var errorType = (msg.includes('gpt_image') && msg.includes('422')) ? 'gpt_image_422'
                        : msg.toLowerCase().includes('timeout') ? 'timeout'
                        : 'generic';
          showErrorModal(errorType, modal);
          if (opts.onFail) opts.onFail(msg);
        } else if (result.status === 'rate_limited') {
          clearIntervals(opts.intervals, opts.key);
          modal.querySelector('.loading-area').style.display = 'none';
          showErrorModal('rate_limited', modal);
          if (opts.onFail) opts.onFail('rate_limited');
        }
      })
      .catch(function(e) { console.error('Error checking job status:', e); })
      .finally(function() { checking = false; });
  }
}

function clearIntervals(intervals, key) {
  if (intervals[key]) {
    clearInterval(intervals[key].progress);
    clearInterval(intervals[key].poll);
    clearInterval(intervals[key].msg);
    intervals[key] = null;
  }
}

function isProcessing(intervals, key) {
  var iv = intervals[key];
  return iv && (iv.progress || iv.poll);
}

// ===== RENDER OPTIONS FORM =====
function renderOptions(container, options) {
  container.innerHTML = '';
  options.forEach(function(opt) {
    var div = document.createElement('div');
    div.className = 'form-group';
    var label = document.createElement('label');
    label.textContent = opt.label + (opt.required ? ' *' : '');
    div.appendChild(label);

    var input = document.createElement('input');
    input.type = 'text';
    input.setAttribute('data-option-key', opt.key);
    input.setAttribute('data-option-label', opt.label || opt.key);
    input.addEventListener('keydown', function(e) { if (e.key === 'Enter') { e.preventDefault(); input.blur(); } });
    if (opt.required) input.required = true;
    if (opt.placeholder) input.placeholder = opt.placeholder;
    if (opt.max) input.maxLength = opt.max;

    if (opt.type === 'text_with_suggestions' && opt.suggestions && opt.suggestions.length > 0) {
      input.style.display = 'none';
      var combo = document.createElement('div');
      combo.className = 'combo option-combo';
      combo.setAttribute('data-option-key', opt.key);
      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'combo-trigger';
      trigger.innerHTML = '<span class="combo-label">Select...</span><span class="combo-chevron">\u25be</span>';
      var popover = document.createElement('div');
      popover.className = 'combo-popover';
      var card = document.createElement('div');
      card.className = 'combo-card';

      opt.suggestions.forEach(function(suggestion) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'combo-option';
        btn.setAttribute('data-value', suggestion);
        btn.innerHTML = '<span>' + suggestion + '</span><span class="check">\u2713</span>';
        card.appendChild(btn);
      });

      var customBtn = document.createElement('button');
      customBtn.type = 'button';
      customBtn.className = 'combo-option';
      customBtn.setAttribute('data-value', 'custom');
      customBtn.innerHTML = '<span>Custom</span><span class="check">\u2713</span>';
      card.appendChild(customBtn);

      popover.appendChild(card);
      combo.appendChild(trigger);
      combo.appendChild(popover);

      card.addEventListener('click', function(e) {
        var optionBtn = e.target.closest('.combo-option');
        if (!optionBtn) return;
        var value = optionBtn.dataset.value;
        card.querySelectorAll('.combo-option').forEach(function(o) { o.classList.remove('active'); });
        optionBtn.classList.add('active');
        if (value === 'custom') { combo.style.display = 'none'; input.style.display = 'block'; input.value = ''; input.focus(); }
        else { combo.querySelector('.combo-label').textContent = value; input.value = value; }
        combo.classList.remove('open');
        trigger.classList.remove('active');
      });

      input.addEventListener('blur', function() {
        if (input.value.trim() === '') {
          input.style.display = 'none'; combo.style.display = '';
          combo.querySelector('.combo-label').textContent = 'Select...';
          card.querySelectorAll('.combo-option').forEach(function(o) { o.classList.remove('active'); });
        }
      });

      div.appendChild(combo);
    }
    div.appendChild(input);
    container.appendChild(div);
  });
}

function collectOptionValues(modal) {
  var values = {};
  modal.querySelectorAll('input[data-option-key]').forEach(function(input) {
    var v = input.value.trim();
    if (v !== '') values[input.getAttribute('data-option-key')] = v;
  });
  return values;
}

function collectOptionLabels(modal) {
  var labels = {};
  modal.querySelectorAll('input[data-option-key]').forEach(function(input) {
    var key = input.getAttribute('data-option-key');
    var label = input.getAttribute('data-option-label');
    if (key && label) labels[key] = label;
  });
  return labels;
}

async function loadProductOptions(handle, container) {
  if (!handle) return;
  try {
    var r = await fetch(API_BASE_URL + '/options?product=' + handle);
    var data = await r.json();
    if (data.options && data.options.length) {
      renderOptions(container, data.options);
      return data.options;
    }
  } catch (e) { console.error(e); }
  return [];
}

// ===== UPLOAD ERROR MESSAGE =====
function uploadErrorMessage(err) {
  var msg = 'Failed to upload image. ';
  if (err.message.includes('timeout') || err.message.includes('abort')) msg += 'Upload timed out. Please try again.';
  else if (err.message.includes('403') || err.message.includes('Forbidden')) msg += 'Upload URL expired. Please try again.';
  else if (err.message.includes('network')) msg += 'Network error. Please check your connection.';
  else msg += 'Please try again.';
  return msg;
}

// ===== INJECT VARIANT CARDS =====
function injectVariantCards(container, productData) {
  container.innerHTML =
    '<div class="vopt-card canvas-card"><div class="vopt-card-header"><span class="vopt-label">ART PRINT</span></div><div class="vopt-card-body"><div class="vopt-features-row"><div class="vopt-feature"><span class="vopt-check">\u2713</span> Ready to hang</div><div class="vopt-feature"><span class="vopt-check">\u2713</span> Digital file included</div></div><div class="print-type-selector"><button type="button" class="print-type-btn active" data-type="canvas"><img src="https://havjoy.com/cdn/shop/files/canvas1_1b90379f-528b-4f5a-a70a-b0d39d7091fc_120x.jpg" alt="Canvas"> Canvas</button><button type="button" class="print-type-btn" data-type="framed"><img src="https://havjoy.com/cdn/shop/files/frame1_120x.jpg" alt="Framed"> Framed</button></div><input type="hidden" class="print-type-input" value="canvas"><p class="size-label">Size</p><div class="size-selector"><button type="button" class="size-btn active" data-value="9x12">9x12</button><button type="button" class="size-btn" data-value="12x16">12x16</button><button type="button" class="size-btn" data-value="16x20">16x20</button></div><input type="hidden" class="combo-input addon-size" data-addon="canvas" value="9x12"><button type="button" class="vopt-btn canvas-atc-btn">Add to Cart &mdash; <span class="canvas-atc-price-text"></span></button><p class="modal-installment-text" style="font-size:1.3rem;padding-left:5px;text-align:center;display:none"><span>Pay in <span class="modal-interest-num">2</span> interest-free installments of <span style="font-weight:700" class="modal-money-installment">$0.00</span> with <span class="inline-flex align-middle text-purple-primary"><svg style="width:59px;height:14px;vertical-align:middle;color:rgb(84 51 235 / 1)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 99 25"><path fill="currentColor" d="M70.842 7.915h2.25c1.561 0 2.328.642 2.328 1.715 0 1.074-.739 1.715-2.259 1.715h-2.32v-3.43ZM80.525 16.142c-.879 0-1.227-.474-1.227-.948 0-.642.725-.935 2.147-1.102l1.115-.125c-.07 1.227-.892 2.175-2.035 2.175Z"></path><path fill="currentColor" fill-rule="evenodd" d="M65.645.5a3.64 3.64 0 0 0-3.64 3.64V20.7a3.64 3.64 0 0 0 3.64 3.64h29.668a3.64 3.64 0 0 0 3.64-3.64V4.14A3.64 3.64 0 0 0 95.314.5H65.645Zm5.197 16.674v-4.197h2.64c2.412 0 3.695-1.353 3.695-3.402 0-2.05-1.283-3.277-3.695-3.277h-4.341v10.876h1.7Zm9.334.223c1.297 0 2.147-.572 2.538-1.548.112 1.088.767 1.645 2.189 1.269l.014-1.157c-.572.055-.683-.154-.683-.753v-2.845c0-1.673-1.102-2.663-3.138-2.663-2.008 0-3.165 1.004-3.165 2.705h1.562c0-.809.572-1.297 1.576-1.297 1.06 0 1.547.46 1.534 1.255v.363l-1.8.195c-2.021.223-3.137.99-3.137 2.329 0 1.101.781 2.147 2.51 2.147Zm9.906.32c-.711 1.73-1.855 2.245-3.64 2.245h-.766V18.54h.822c.977 0 1.45-.307 1.966-1.185L85.3 9.923h1.757l2.259 5.424 2.008-5.424h1.715l-2.956 7.795Z" clip-rule="evenodd"></path><path fill="currentColor" d="M6.992 11.055c-2.359-.509-3.41-.708-3.41-1.612 0-.85.711-1.274 2.134-1.274 1.25 0 2.165.544 2.839 1.61.05.081.155.11.241.066l2.655-1.335a.186.186 0 0 0 .076-.259c-1.102-1.9-3.137-2.94-5.818-2.94C2.188 5.311 0 7.037 0 9.781c0 2.915 2.664 3.651 5.027 4.16 2.362.51 3.417.709 3.417 1.613s-.769 1.33-2.303 1.33c-1.416 0-2.467-.644-3.102-1.896a.186.186 0 0 0-.251-.082L.14 16.21a.188.188 0 0 0-.083.253c1.051 2.102 3.207 3.285 6.087 3.285 3.668 0 5.885-1.698 5.885-4.527 0-2.83-2.677-3.651-5.037-4.16v-.007ZM21.218 5.311c-1.505 0-2.835.531-3.791 1.477-.06.057-.159.015-.159-.067V.687A.185.185 0 0 0 17.081.5h-3.322a.185.185 0 0 0-.187.187v18.73c0 .104.083.186.187.186h3.322a.185.185 0 0 0 .187-.186V11.2c0-1.587 1.223-2.804 2.87-2.804 1.649 0 2.843 1.191 2.843 2.804v8.216c0 .104.082.186.187.186h3.322a.185.185 0 0 0 .187-.186V11.2c0-3.452-2.274-5.89-5.459-5.89ZM33.415 4.774c-1.803 0-3.493.55-4.706 1.343a.186.186 0 0 0-.06.25l1.464 2.488c.054.089.168.12.257.066a5.853 5.853 0 0 1 3.052-.834c2.899 0 5.03 2.036 5.03 4.726 0 2.292-1.706 3.99-3.868 3.99-1.762 0-2.985-1.022-2.985-2.463 0-.825.352-1.502 1.27-1.98a.183.183 0 0 0 .073-.258l-1.381-2.327a.187.187 0 0 0-.226-.079c-1.85.683-3.15 2.327-3.15 4.533 0 3.338 2.67 5.83 6.396 5.83 4.35 0 7.478-3 7.478-7.303 0-4.612-3.64-7.982-8.644-7.982ZM51.776 5.283c-1.68 0-3.182.62-4.277 1.707a.093.093 0 0 1-.16-.066v-1.31a.185.185 0 0 0-.187-.186h-3.235a.185.185 0 0 0-.188.187v18.702c0 .104.083.186.188.186h3.32a.185.185 0 0 0 .188-.186v-6.133c0-.082.099-.123.16-.07 1.091 1.012 2.536 1.603 4.19 1.603 3.897 0 6.936-3.139 6.936-7.217 0-4.078-3.042-7.217-6.935-7.217Zm-.63 11.266c-2.215 0-3.895-1.754-3.895-4.074S48.928 8.4 51.147 8.4c2.22 0 3.893 1.726 3.893 4.075 0 2.348-1.651 4.074-3.896 4.074h.003Z"></path></svg></span></span></p></div></div>' +
    '<div class="vopt-card"><div class="vopt-card-header"><span class="vopt-label">DIGITAL FILE</span></div><div class="vopt-card-body"><div class="vopt-product-row"><img class="vopt-product-img" src="' + (productData.image || '') + '" alt="' + productData.title + '"><div class="vopt-product-info"><div class="vopt-feature"><span class="vopt-check">\u2713</span> Instant download</div><div class="vopt-feature"><span class="vopt-check">\u2713</span> High Resolution</div><div class="vopt-feature"><span class="vopt-check">\u2713</span> Print & Share unlimited</div></div></div><button type="button" class="vopt-btn download-now-btn">Download Now &mdash; <span class="vopt-product-price digital-base-price" data-base-price="' + productData.variants[0].price + '">' + formatMoney(productData.variants[0].price) + '</span></button></div></div>' +
    '<div class="vopt-packs-view" style="display:none"><h4 class="vopt-packs-heading">Choose Your Pack</h4><div class="vopt-pack-mini-card"><div class="vopt-pack-mini-top"><div><div class="vopt-pack-mini-title">Digital Pack</div><div class="vopt-pack-mini-sub">5 digital files &middot; $7.99/file</div></div><div class="vopt-pack-mini-price">$39.95</div></div><ul class="vopt-pack-mini-features"><li>\u2713 5 digital files included</li><li>\u2713 Instant high-res download</li><li>\u2713 Yours to keep forever</li></ul><button type="button" class="vopt-btn" data-variant-id="48213661581544" onclick="havjoyPackAtc(this)"><span>Get Digital Pack &mdash; $39.95</span></button></div><div class="vopt-pack-mini-card vopt-pack-mini-card--highlight"><div class="vopt-pack-mini-badge">BEST VALUE</div><div class="vopt-pack-mini-top"><div><div class="vopt-pack-mini-title">Studio Pack</div><div class="vopt-pack-mini-sub">20 digital files &middot; $4.99/file</div></div><div class="vopt-pack-mini-price">$99.95</div></div><ul class="vopt-pack-mini-features"><li>\u2713 20 digital files included</li><li>\u2713 Instant high-res download</li><li>\u2713 Yours to keep forever</li></ul><button type="button" class="vopt-btn" data-variant-id="48213661614312" onclick="havjoyPackAtc(this)"><span>Get Studio Pack &mdash; $99.95</span></button></div></div>';
  var downloadBtn = container.querySelector('.download-now-btn');
  if (downloadBtn && !downloadBtn.querySelector('.vopt-btn__icon')) {
    downloadBtn.insertAdjacentHTML('afterbegin', '<svg class="vopt-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>');
  }
}

// ===== PUBLIC API =====
return {
  API_BASE_URL: API_BASE_URL,
  VARIANT_DIGITAL: VARIANT_DIGITAL,
  SIZE_MAP: SIZE_MAP,
  MAX_GENERATED: MAX_GENERATED,
  Uploader: Uploader,
  showToast: showToast,
  lockPageScroll: lockPageScroll,
  unlockPageScroll: unlockPageScroll,
  formatMoney: formatMoney,
  buildCartProperties: buildCartProperties,
  submitToCart: submitToCart,
  variantTitle: variantTitle,
  sizeFromTitle: sizeFromTitle,
  isOptimalJpeg: isOptimalJpeg,
  convertToJpeg: convertToJpeg,
  showUploadStatus: showUploadStatus,
  updateUploadStatus: updateUploadStatus,
  hideUploadStatus: hideUploadStatus,
  handleFileUpload: handleFileUpload,
  processImage: processImage,
  clearIntervals: clearIntervals,
  isProcessing: isProcessing,
  renderOptions: renderOptions,
  collectOptionValues: collectOptionValues,
  collectOptionLabels: collectOptionLabels,
  loadProductOptions: loadProductOptions,
  uploadErrorMessage: uploadErrorMessage,
  saveSessionToStorage: saveSessionToStorage,
  saveRecentUploadedUrls: saveRecentUploadedUrls,
  normalizeUploadedUrls: normalizeUploadedUrls,
  getLatestUploadedUrls: getLatestUploadedUrls,
  setModalStep: setModalStep,
  syncButtonUI: syncButtonUI,
  injectVariantCards: injectVariantCards
};

})();

function havjoyShowPacksView(el) {
  var variantOption = el.closest('.variant-option');
  var view = variantOption.querySelector('.vopt-packs-view');
  var isOpen = view.style.display !== 'none';
  view.style.display = isOpen ? 'none' : 'block';
  el.querySelector('strong').textContent = isOpen ? 'View Packs & Pricing \u2192' : 'Hide Packs \u00d7';
}
function havjoyPackAtc(btn) {
  var variantId = btn.dataset.variantId;
  var span = btn.querySelector('span');
  var orig = span ? span.textContent : btn.textContent;
  btn.disabled = true;
  if (span) span.textContent = '...'; else btn.textContent = '...';

  var activeModal = document.querySelector('dialog.personalize-modal[open]');
  if (activeModal) activeModal.close();
  var errorModal = document.getElementById('havjoy-error-modal');
  if (errorModal) errorModal.remove();

  if (typeof CartDrawerController !== 'undefined') {
    CartDrawerController.addItem(variantId, 1)
      .then(function() {
        if (span) span.textContent = 'Added!';
        setTimeout(function() { if (span) span.textContent = orig; btn.disabled = false; }, 2500);
      })
      .catch(function() { if (span) span.textContent = orig; btn.disabled = false; });
  } else {
    window.HavjoyCore.submitToCart([{ id: variantId, quantity: 1 }])
      .catch(function() { if (span) span.textContent = orig; btn.disabled = false; });
  }
}
